/**
 * SelectPersonModal Component
 * 
 * Modal simplificado para seleção de pessoa (Fornecedor, Cliente ou Usuário).
 * Design minimalista e direto.
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import { X, Search, Building2, User, Users, Check } from 'lucide-react'
import { 
  supplierService, 
  customerService, 
  userService 
} from '../../../services/erpApi'
import './SelectPersonModal.css'

const LIST_LIMIT = 1000
const SEARCH_LIMIT = 100

const ALL_PERSON_TYPES = ['supplier', 'customer', 'user']

function SelectPersonModal({
  isOpen,
  onClose,
  onSelect,
  selectedType,
  selectedId,
  allowedTypes = ALL_PERSON_TYPES
}) {
  const visibleTypes = useMemo(
    () => (allowedTypes.length > 0 ? allowedTypes : ALL_PERSON_TYPES),
    [allowedTypes]
  )
  const initialTab = visibleTypes.includes(selectedType) ? selectedType : visibleTypes[0]

  const [activeTab, setActiveTab] = useState(initialTab)
  const [searchTerm, setSearchTerm] = useState('')
  const [loading, setLoading] = useState(false)
  const [items, setItems] = useState([])

  const loadItems = useCallback(async (tab, search) => {
    setLoading(true)
    try {
      const trimmedSearch = search.trim()
      const params = {
        limit: trimmedSearch ? SEARCH_LIMIT : LIST_LIMIT,
        ...(trimmedSearch ? { search: trimmedSearch } : {})
      }

      let response
      switch (tab) {
        case 'supplier':
          response = await supplierService.getAll(params)
          break
        case 'customer':
          response = await customerService.getAll({ ...params, status: 'active' })
          break
        case 'user':
          response = await userService.getAll(params)
          break
        default:
          response = { success: false }
      }

      if (response.success) {
        setItems(response.data || [])
      } else {
        setItems([])
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error)
      setItems([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!isOpen) {
      setSearchTerm('')
      setItems([])
      return
    }

    if (selectedType && visibleTypes.includes(selectedType)) {
      setActiveTab(selectedType)
    } else {
      setActiveTab(visibleTypes[0])
    }
  }, [isOpen, selectedType, visibleTypes])

  useEffect(() => {
    if (!isOpen) return

    const timeoutId = setTimeout(() => {
      loadItems(activeTab, searchTerm)
    }, searchTerm.trim() ? 300 : 0)

    return () => clearTimeout(timeoutId)
  }, [isOpen, activeTab, searchTerm, loadItems])

  const handleTabChange = (tab) => {
    setActiveTab(tab)
    setSearchTerm('')
  }

  const handleSelect = (item) => {
    onSelect({
      type: activeTab,
      id: item.id,
      name: item.name,
      email: item.email,
      cpfCnpj: item.cpf_cnpj || item.cpfCnpj
    })
    onClose()
  }

  if (!isOpen) return null

  const tabLabels = {
    supplier: 'Fornecedor',
    customer: 'Cliente',
    user: 'Usuário'
  }

  return (
    <div className="select-person-modal-overlay" onClick={onClose}>
      <div className="select-person-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="select-person-modal-header">
          <h3 className="select-person-modal-title">Selecionar {tabLabels[activeTab]}</h3>
          <button className="select-person-modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* Tabs */}
        <div className="select-person-modal-tabs">
          {visibleTypes.includes('supplier') && (
            <button
              className={`select-person-modal-tab ${activeTab === 'supplier' ? 'active' : ''}`}
              onClick={() => handleTabChange('supplier')}
            >
              <Building2 size={16} />
              Fornecedores
            </button>
          )}
          {visibleTypes.includes('customer') && (
            <button
              className={`select-person-modal-tab ${activeTab === 'customer' ? 'active' : ''}`}
              onClick={() => handleTabChange('customer')}
            >
              <Users size={16} />
              Clientes
            </button>
          )}
          {visibleTypes.includes('user') && (
            <button
              className={`select-person-modal-tab ${activeTab === 'user' ? 'active' : ''}`}
              onClick={() => handleTabChange('user')}
            >
              <User size={16} />
              Usuários
            </button>
          )}
        </div>

        {/* Search */}
        <div className="select-person-modal-search">
          <Search size={18} className="select-person-modal-search-icon" />
          <input
            type="text"
            placeholder={`Buscar ${tabLabels[activeTab].toLowerCase()}...`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="select-person-modal-search-input"
            autoFocus
          />
        </div>

        {/* Content */}
        <div className="select-person-modal-content">
          {loading ? (
            <div className="select-person-modal-empty">
              <p>Carregando...</p>
            </div>
          ) : items.length === 0 ? (
            <div className="select-person-modal-empty">
              <p>
                {searchTerm.trim()
                  ? `Nenhum resultado encontrado para "${searchTerm}"`
                  : 'Nenhum resultado encontrado'}
              </p>
            </div>
          ) : (
            <div className="select-person-modal-list">
              {items.map((item) => {
                const isSelected = selectedId === item.id && selectedType === activeTab
                return (
                  <button
                    key={item.id}
                    type="button"
                    className={`select-person-modal-item ${isSelected ? 'selected' : ''}`}
                    onClick={() => handleSelect(item)}
                  >
                    <div className="select-person-modal-item-content">
                      <span className="select-person-modal-item-name">{item.name}</span>
                      {item.email && (
                        <span className="select-person-modal-item-email">{item.email}</span>
                      )}
                      {item.cpf_cnpj && (
                        <span className="select-person-modal-item-doc">{item.cpf_cnpj}</span>
                      )}
                    </div>
                    {isSelected && (
                      <Check size={18} className="select-person-modal-item-check" />
                    )}
                  </button>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default SelectPersonModal
